import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Icon, Input } from "semantic-ui-react";

type CardTransferListProps = {
  disabled?: boolean;
  list?: ListType[];
  selected?: (number | string)[];
  showRightOnly?: boolean;
  textKey: "name" | "text";
  valueKey: "id" | "value" | "text";
  onSelected?: (selected: (number | string)[]) => any;
};

type ListType = {
  id?: number;
  [key: string]: any;
  name?: string;
  text?: string;
  value?: number | string;
};

const CardTransferList = (props: CardTransferListProps) => {
  const [checkedLeft, setCheckedLeft] = useState<number[]>([]);
  const [checkedRight, setCheckedRight] = useState<number[]>([]);

  const list = useMemo(
    () => (props.list || []).map((item, index) => ({ ...item, index })),
    [props.list]
  );

  const leftList = useMemo(
    () => list.filter((item) => !(props.selected || []).includes(item[props.valueKey] || "")),
    [list, props.selected, props.valueKey]
  );

  const rightList = useMemo(
    () => list.filter((item) => (props.selected || []).includes(item[props.valueKey] || "")),
    [list, props.selected, props.valueKey]
  );

  const handleTransfer = (action: "left" | "right") => {
    if (action === "right") {
      // ย้ายจากซ้ายไปขวา - เพิ่มรายการที่เลือกเข้าไปใน selected
      props.onSelected?.([
        ...(props.selected || []),
        ...list.flatMap((item, index) =>
          checkedLeft.includes(index) ? [item[props.valueKey] || ""] : []
        ),
      ]);
    } else {
      // ย้ายจากขวาไปซ้าย - ลบรายการที่เลือกออกจาก selected
      const checked = new Set(
        list.flatMap((item, index) =>
          checkedRight.includes(index) ? [item[props.valueKey] || ""] : []
        )
      );

      props.onSelected?.((props.selected || []).filter((id) => !checked.has(id)));
    }

    setCheckedLeft([]);
    setCheckedRight([]);
  };

  return (
    <div style={{ alignItems: "center", display: "flex" }}>
      {!props.showRightOnly && (
        <>
          <ListBox
            checked={checkedLeft}
            disabled={props.disabled}
            list={leftList}
            textKey={props.textKey}
            valueKey={props.valueKey}
            // callback
            onChecked={setCheckedLeft}
          />
          <div style={{ padding: "0 1rem" }}>
            <TransferIcon
              action="right"
              disabled={props.disabled || checkedLeft.length === 0}
              onClickTransfer={handleTransfer}
            />
            <TransferIcon
              action="left"
              disabled={props.disabled || checkedRight.length === 0}
              onClickTransfer={handleTransfer}
            />
          </div>
        </>
      )}
      <ListBox
        checked={checkedRight}
        disabled={props.disabled}
        list={rightList}
        textKey={props.textKey}
        valueKey={props.valueKey}
        hideSearch
        onChecked={setCheckedRight}
      />
    </div>
  );
};

/* ------------------------------------------------------ */
/*                       TransferIcon                     */
/* ------------------------------------------------------ */
type TransferIconProps = {
  action: "left" | "right";
  disabled?: boolean;
  onClickTransfer: (action: "left" | "right") => any;
};

const TransferIcon = (props: TransferIconProps) => {
  const handleClick = () => {
    if (!props.disabled) {
      props.onClickTransfer(props.action);
    }
  };

  return (
    <div
      style={{
        alignItems: "flex-end",
        cursor: props.disabled ? "" : "pointer",
        display: "flex",
        height: "50%",
        marginBottom: "10px",
        opacity: props.disabled ? "0.5" : "",
      }}
    >
      <Icon name={`arrow circle ${props.action}`} size="large" onClick={handleClick} />
    </div>
  );
};

/* ------------------------------------------------------ */
/*                        ListBox;                        */
/* ------------------------------------------------------ */
type ListBoxProps = Pick<CardTransferListProps, "textKey" | "valueKey"> & {
  checked: number[];
  disabled?: boolean;
  hideSearch?: boolean;
  list: (ListType & { index: number })[];
  onChecked: (checked: number[]) => any;
};

const ListBox = (props: ListBoxProps) => {
  const [currentChecked, setCurrentChecked] = useState<number>(-1);
  const [list, setList] = useState<ListType[]>([]);
  const [search, setSearch] = useState<string>("");

  // States สำหรับการลากเมาส์เพื่อเลือกหลายรายการ
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<number>(-1);

  const currentKeyPressRef = useRef<string | null>(null);

  useEffect(() => {
    const filterList = props.list.filter((item) => {
      const value: string = item[props.textKey as string] || "";

      return value.toLowerCase().includes(search.toLowerCase());
    });

    setList(filterList);
  }, [props.list, search]);

  const handleKeyup = useCallback(() => {
    currentKeyPressRef.current = "";
  }, []);

  const handleKeydown = useCallback((ev: KeyboardEvent) => {
    currentKeyPressRef.current = ev.key;
  }, []);

  // Handle mouse press down - start drag selection
  const handleMouseDown = useCallback(
    (index: number, e: React.MouseEvent) => {
      const isDisabled = props.disabled;
      const isLeftMouseButton = e.button === 0;
      const isControlPressed = currentKeyPressRef.current === "Control";
      const isShiftPressed = currentKeyPressRef.current === "Shift";

      // ข้ามการทำงานเมื่อ: disabled, ไม่ใช่คลิกซ้าย, หรือกดปุ่ม Control/Shift
      if (isDisabled || !isLeftMouseButton || isControlPressed || isShiftPressed) {
        return;
      }

      // Start drag selection for normal clicks
      setIsDragging(true);
      setDragStart(index);
    },
    [props.disabled]
  );

  // Handle mouse enter on items - used for drag selection
  const handleMouseEnter = useCallback(
    (index: number, e: React.MouseEvent) => {
      const isDisabled = props.disabled;
      const isLeftMouseButtonPressed = e.buttons === 1;
      const hasNoKeyboardModifiers = !currentKeyPressRef.current;

      // ทำ drag selection เมื่อ:
      // 1. ไม่ได้ disabled
      // 2. อยู่ในโหมด drag
      // 3. กดปุ่มเมาส์ซ้ายค้างไว้
      // 4. ไม่มีการกดปุ่ม keyboard modifiers
      if (isDisabled || !(isDragging && isLeftMouseButtonPressed && hasNoKeyboardModifiers)) {
        return;
      }

      // Calculate the range of items to select while dragging
      const start = Math.min(dragStart, index);
      const end = Math.max(dragStart, index);
      const dragSelection = Array.from(
        { length: end - start + 1 },
        (unused, cIndex) => start + cIndex
      );

      props.onChecked(dragSelection);
    },
    [dragStart, isDragging, props.disabled, props.onChecked]
  );

  // Handle mouse release - end drag selection
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setDragStart(-1);
    }
  }, [isDragging]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    document.addEventListener("keyup", handleKeyup);

    return () => {
      document.removeEventListener("keydown", handleKeydown);
      document.removeEventListener("keyup", handleKeyup);
    };
  }, [handleKeydown, handleKeyup]);

  const handleSearch = (e: any, data: any) => {
    if (props.disabled) {
      return;
    }

    props.onChecked([]);
    setSearch(data.value);
  };

  const handleSelected = (selectIndex: number) => {
    if (props.disabled) {
      return;
    }

    const checked = props.checked;

    // กรณีกด Control - เลือก/ยกเลิกรายการเดี่ยว
    if (currentKeyPressRef.current === "Control") {
      let checkedIndexes = [...checked];

      if (checked.includes(selectIndex)) {
        checkedIndexes = checkedIndexes.filter((checkedId) => checkedId !== selectIndex);
      } else {
        checkedIndexes.push(selectIndex);
      }

      setCurrentChecked(selectIndex);
      props.onChecked(checkedIndexes);

      return;
    }

    // กรณีกด Shift - เลือกช่วงรายการ
    if (currentKeyPressRef.current === "Shift" && checked.length > 0) {
      props.onChecked(
        Array.from({ length: Math.abs(currentChecked - selectIndex) + 1 })
          .fill("")
          .map((unused, index) => Math.min(currentChecked, selectIndex) + index)
      );
    } else {
      // การเลือกปกติ - toggle รายการเดี่ยว
      props.onChecked(checked.length === 1 && checked.includes(selectIndex) ? [] : [selectIndex]);
      setCurrentChecked(selectIndex);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "white",
        border: "1px solid rgba(34, 36, 38, 0.25)",
        borderRadius: "15px",
        height: "180px",
        width: "235px",
        // opacity: props.disabled ? 0.6 : 1,
      }}
    >
      <div style={{ margin: "10px" }}>
        {!props.hideSearch && (
          <div style={{ display: "flex", width: "100%" }}>
            <Input
              disabled={props.disabled}
              icon="search"
              placeholder="Filter"
              style={{ width: "100%" }}
              value={search}
              fluid
              onChange={handleSearch}
            />
          </div>
        )}
        <div
          className="box-list"
          style={{
            height: props.hideSearch ? "168px" : "125px",
            marginTop: "5px",
            overflow: "auto",
            paddingBottom: "10px",
            userSelect: "none",
          }}
        >
          {list.map((item) => (
            <div
              key={`list${item.index}`}
              aria-hidden="true"
              onClick={() => {
                handleSelected(item.index);

                // รีเซ็ต drag state หลังจากคลิก
                setIsDragging(false);
                setDragStart(-1);
              }}
              onMouseDown={(e) => {
                handleMouseDown(item.index, e);
              }}
              onMouseEnter={(e) => {
                handleMouseEnter(item.index, e);
              }}
              onMouseUp={handleMouseUp}
              style={{
                backgroundColor: props.checked.includes(item.index) ? "#C6EBF3" : "",
                cursor: props.disabled ? "default" : "pointer",
                padding: "1px 3px",
                // opacity: props.disabled ? 0.6 : 1,
              }}
            >
              {item[props.textKey]}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

CardTransferList.displayName = "CardTransferList";

export default React.memo(CardTransferList);
